/**
 * Nomenclature INSEE des catégories juridiques (niveau III) - mapping code
 * numérique → label court/long.
 *
 * Source : INSEE, Nomenclature des catégories juridiques - Niveau III (2024).
 * https://www.insee.fr/fr/information/2028129
 *
 * On embarque les ~100 codes les plus fréquents en France métropolitaine
 * (>98 % des prospects B2B en courtage assurance). Les codes non mappés
 * retombent sur leur représentation brute (ex: "5632") - l'utilisateur voit
 * le code nu plutôt qu'un libellé inventé. Pour ajouter un code : éditer ce
 * fichier et pousser - pas de régénération de données stockées nécessaire
 * (le code INSEE brut est ce qui est persisté dans prospects.legal_form).
 */

export type LegalFormEntry = {
  /** Label court affiché par défaut dans les listes / cartes (ex: "SARL"). */
  short: string;
  /** Label long pour tooltip / contextes où on veut la dénomination exacte. */
  long: string;
};

export const LEGAL_FORMS: Readonly<Record<string, LegalFormEntry>> = {
  // -------------------------------------------------------------------------
  // Entrepreneurs individuels
  // -------------------------------------------------------------------------
  '1000': { short: 'EI', long: 'Entrepreneur individuel' },

  // -------------------------------------------------------------------------
  // Sociétés en nom collectif (SNC)
  // -------------------------------------------------------------------------
  '5202': { short: 'SNC', long: 'Société en nom collectif' },
  '5203': { short: 'SNC', long: 'Société en nom collectif (autre)' },

  // -------------------------------------------------------------------------
  // Sociétés en commandite
  // -------------------------------------------------------------------------
  '5306': { short: 'SCS', long: 'Société en commandite simple' },
  '5307': { short: 'SCA', long: 'Société en commandite par actions' },
  '5308': { short: 'SCA', long: 'Société en commandite par actions (autre)' },
  '5385': { short: 'SC', long: 'Société en commandite, autre' },

  // -------------------------------------------------------------------------
  // SARL et variantes (code 5400-5499)
  // -------------------------------------------------------------------------
  '5410': { short: 'SARL', long: 'SARL nationale' },
  '5415': { short: 'SARL', long: "SARL d'économie mixte" },
  '5422': { short: 'SARL', long: 'SARL immobilière de copropriété' },
  '5426': { short: 'SARL', long: "SARL immobilière d'attribution" },
  '5430': { short: 'SARL', long: "SARL d'exploitation agricole" },
  '5431': { short: 'SARL HLM', long: 'SARL de construction de logements à loyer modéré' },
  '5432': { short: 'EARL', long: 'Exploitation agricole à responsabilité limitée' },
  '5442': { short: 'EURL', long: 'SARL unipersonnelle (EURL)' },
  '5443': { short: 'EURL', long: 'SARL unipersonnelle à associé non gérant' },
  '5451': { short: 'SARL coop', long: 'SARL coopérative de construction' },
  '5453': { short: 'SARL coop', long: 'SARL coopérative de consommation' },
  '5454': { short: 'SARL coop', long: 'SARL coopérative artisanale' },
  '5455': { short: 'SARL coop', long: "SARL coopérative d'intérêt maritime" },
  '5458': { short: 'SARL coop', long: 'SARL coopérative de transport' },
  '5459': { short: 'SCOP SARL', long: 'SARL coopérative ouvrière (SCOP)' },
  '5460': { short: 'SARL coop', long: 'SARL coopérative (autre)' },
  '5485': { short: 'SELARL', long: "Société d'exercice libéral à responsabilité limitée" },
  '5498': { short: 'EURL', long: 'SARL unipersonnelle' },
  '5499': { short: 'SARL', long: 'Société à responsabilité limitée' },

  // -------------------------------------------------------------------------
  // SA (code 5500-5599) - sociétés anonymes, à conseil d'administration
  // -------------------------------------------------------------------------
  '5505': { short: 'SA', long: "SA à participation ouvrière à conseil d'administration" },
  '5510': { short: 'SA', long: "SA nationale à conseil d'administration" },
  '5515': { short: 'SEM', long: "SA d'économie mixte à conseil d'administration" },
  '5520': { short: 'Fonds', long: "Fonds à forme sociétaire à conseil d'administration" },
  '5522': { short: 'SA HLM', long: "SA d'HLM à conseil d'administration" },
  '5525': { short: 'SA coop', long: "SA coopérative de construction à conseil d'administration" },
  '5530': { short: 'SA', long: "SA de crédit immobilier à conseil d'administration" },
  '5531': { short: 'SA coop', long: 'SA coopérative de production de HLM' },
  '5532': { short: 'SA coop', long: 'SA coopérative de consommation' },
  '5542': { short: 'SCOP', long: 'SCOP - SA coopérative ouvrière de production' },
  '5543': { short: 'SCIC', long: "SCIC - SA coopérative d'intérêt collectif" },
  '5546': { short: 'SA', long: 'SA de crédit maritime mutuel' },
  '5547': { short: 'SA', long: "SA à conseil d'administration" },
  '5548': { short: 'SA', long: "SA à conseil d'administration à mission" },
  '5551': { short: 'SA', long: 'SA à participation ouvrière à directoire' },
  '5552': { short: 'SA', long: 'SA à directoire' },
  '5553': { short: 'SA', long: "SA d'économie mixte à directoire" },
  '5554': { short: 'SA', long: 'SA nationale à directoire' },
  '5555': { short: 'SA HLM', long: "SA d'HLM à directoire" },
  '5558': { short: 'SA coop', long: 'SA coopérative de construction à directoire' },
  '5559': { short: 'SA', long: 'SA de crédit immobilier à directoire' },
  '5560': { short: 'SA coop', long: 'SA coopérative de consommation à directoire' },
  '5585': { short: 'SELAFA', long: "Société d'exercice libéral à forme anonyme" },
  '5599': { short: 'SA', long: 'Société anonyme (autre)' },

  // -------------------------------------------------------------------------
  // SAS (code 5700-5799) - sociétés par actions simplifiées
  // -------------------------------------------------------------------------
  '5710': { short: 'SAS', long: 'Société par actions simplifiée' },
  '5720': { short: 'SASU', long: 'Société par actions simplifiée unipersonnelle' },
  '5785': { short: 'SELAS', long: "Société d'exercice libéral par actions simplifiée" },

  // -------------------------------------------------------------------------
  // Société européenne
  // -------------------------------------------------------------------------
  '5800': { short: 'SE', long: 'Société européenne' },

  // -------------------------------------------------------------------------
  // GIE / GEIE
  // -------------------------------------------------------------------------
  '6100': { short: "Caisse d'épargne", long: "Caisse d'épargne et de prévoyance" },
  '6200': { short: 'GIE', long: "Groupement d'intérêt économique" },
  '6210': { short: 'GEIE', long: "Groupement européen d'intérêt économique" },
  '6220': { short: 'GIE', long: 'GIE (autre)' },

  // -------------------------------------------------------------------------
  // Sociétés civiles (code 6500-6599)
  // -------------------------------------------------------------------------
  '6521': { short: 'SCPI', long: 'Société civile de placement collectif immobilier' },
  '6532': { short: 'SCEA', long: "Société civile d'exploitation agricole" },
  '6533': { short: 'GAEC', long: "Groupement agricole d'exploitation en commun" },
  '6534': { short: 'GFR', long: 'Groupement foncier rural' },
  '6535': { short: 'GFA', long: 'Groupement foncier agricole' },
  '6537': { short: 'GFF', long: 'Groupement foncier forestier' },
  '6540': { short: 'SCI', long: 'Société civile immobilière' },
  '6541': { short: 'SCI', long: 'Société civile immobilière de construction vente' },
  '6542': { short: 'SCI', long: "Société civile immobilière d'attribution" },
  '6543': { short: 'SCEA', long: "Société civile d'exploitation agricole" },
  '6544': { short: 'SCA', long: "Société civile d'attribution" },
  '6550': { short: 'SCM', long: 'Société civile de moyens' },
  '6551': { short: 'SCPI', long: 'Société civile de placement immobilier' },
  '6558': { short: 'SC', long: 'Société civile laitière' },
  '6560': { short: 'SC', long: 'Autre société civile' },
  '6561': { short: 'SCP', long: "SCP d'avocats" },
  '6562': { short: 'SCP', long: "SCP d'avocats aux conseils" },
  '6563': { short: 'SCP', long: "SCP d'avoués d'appel" },
  '6564': { short: 'SCP', long: "SCP d'huissiers" },
  '6565': { short: 'SCP', long: 'SCP de notaires' },
  '6566': { short: 'SCP', long: 'SCP de commissaires-priseurs' },
  '6567': { short: 'SCP', long: 'SCP de greffiers de tribunal de commerce' },
  '6568': { short: 'SCP', long: 'SCP de conseils juridiques' },
  '6569': { short: 'SCP', long: 'SCP de commissaires aux comptes' },
  '6571': { short: 'SCP', long: 'SCP de médecins' },
  '6572': { short: 'SCP', long: 'SCP de dentistes' },
  '6573': { short: 'SCP', long: "SCP d'infirmiers" },
  '6574': { short: 'SCP', long: 'SCP de masseurs-kinésithérapeutes' },
  '6575': { short: 'SCP', long: "SCP de directeurs de laboratoire d'analyse médicale" },
  '6576': { short: 'SCP', long: 'SCP de vétérinaires' },
  '6577': { short: 'SCP', long: 'SCP de géomètres experts' },
  '6578': { short: 'SCP', long: "SCP d'architectes" },
  '6585': { short: 'SCP', long: 'Autre société civile professionnelle' },

  // -------------------------------------------------------------------------
  // Administrations, établissements publics, mutuelles, syndicats
  // -------------------------------------------------------------------------
  '7150': { short: 'Service central', long: "Service central d'un ministère" },
  '7320': { short: 'EPN', long: 'Établissement public national administratif' },
  '7381': { short: 'Org. consulaire', long: 'Organisme consulaire' },
  '8110': {
    short: 'EPIC',
    long: 'Établissement public national à caractère industriel ou commercial',
  },
  '8120': { short: 'EPA', long: 'Établissement public national à caractère administratif' },
  '8210': { short: 'Mutuelle', long: 'Mutualité' },
  '8250': { short: 'Syndicat', long: 'Syndicat professionnel' },

  // -------------------------------------------------------------------------
  // Associations et fondations
  // -------------------------------------------------------------------------
  '9110': { short: 'Syndicat copro.', long: 'Syndicat de copropriété' },
  '9210': { short: 'Asso.', long: 'Association non déclarée' },
  '9220': { short: 'Asso.', long: 'Association déclarée' },
  '9221': { short: 'Asso.', long: "Association déclarée d'insertion par l'économique" },
  '9222': { short: 'Asso.', long: 'Association intermédiaire' },
  '9223': { short: 'GE', long: "Groupement d'employeurs" },
  '9224': {
    short: 'Asso. avocats',
    long: "Association d'avocats à responsabilité professionnelle individuelle",
  },
  '9230': { short: 'Asso. RUP', long: "Association déclarée, reconnue d'utilité publique" },
  '9260': { short: 'Asso. locale', long: 'Association de droit local (Alsace-Moselle)' },
  '9300': { short: 'Fondation', long: 'Fondation' },
};

/**
 * Retourne le label court (ex: "SARL") pour un code INSEE. Si le code n'est
 * pas connu, retourne le code brut passé en entrée (fallback transparent).
 * Input `null`/`undefined`/vide → `null`.
 */
export function legalFormShort(code: string | null | undefined): string | null {
  if (!code) return null;
  return LEGAL_FORMS[code]?.short ?? code;
}

/**
 * Retourne le label long (dénomination complète) pour un code INSEE. Si le
 * code n'est pas connu, retourne `null` (pas de fallback - un tooltip vide
 * vaut mieux qu'un code numérique répété).
 */
export function legalFormLong(code: string | null | undefined): string | null {
  if (!code) return null;
  return LEGAL_FORMS[code]?.long ?? null;
}
